import React from 'react';
import { connect } from 'react-redux';
import { provideIntlService, provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { loadMyStudiesCycleList, sortMyStudiesCyclesList } from '../actions/my-studies-cycle-list-actions';
import { Grid, InlineBadge, DatePeriodFormatter } from '../../shared/components';
import { getStudentCycleStatusColor } from '../../../utils/studyPlanCycleDepartmentStudentStatusColors';
import { withRouter } from '../../../components/withRouter';
import styled from 'styled-components';
import { Text, WrapLayout, Colors } from '../../../ui';

const NoWrapContainer = styled.div`
  white-space: pre-wrap;
`;

const GroupHeaderStatsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-left: 12px;
  font-weight: 600;
`;

const GroupHeaderContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CycleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CaptionText = styled(Text).attrs(props => ({
  textColor: Colors.GRAY_50,
  variant: 'caption'
}))`
  width: 50%
`;

const ClickableTr = styled.tr`
  cursor: pointer;
`;

class MyStudiesCycleList extends React.Component {
  constructor(props) {
    super(props);
    this.expandChange = this.expandChange.bind(this);
    this.renderCell = this.renderCell.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.getRowLink = this.getRowLink.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.renderCycle = this.renderCycle.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
  }

  componentDidMount() {
    this.props.loadMyStudiesCycleList(this.props.studentId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.studentId !== this.props.studentId) {
      this.props.loadMyStudiesCycleList(this.props.studentId);
    }
  }

  render() {
    const { myCycles, loading } = this.props;
    const localizationService = provideLocalizationService(this);
    return (
      <Grid
        loading={loading}
        data={myCycles}
        expandField='expanded'
        group={[{ field: 'year' }]}
        onExpandChange={this.expandChange}
        cellRender={this.renderCell}
        onRowClick={this.handleRowClick}
        rowLink={this.getRowLink}
        sort={this.props.sorting}
        onSortChange={this.handleSort}
        rowRender={(tr, props) => {
          if (tr && props.rowType === 'groupHeader') {
            return <ClickableTr {...tr.props} className=''>{tr.props.children}</ClickableTr>;
          }
          return tr;
        }}
        isPageable={false}
        columns={[
          {
            field: 'cycle',
            className: 'align-left',
            headerClassName: 'align-left',
            title: localizationService.toLanguageString('myStudies.cycle'),
            cell: this.renderCycle,
            sortable: false
          }, {
            field: 'status',
            className: 'align-left',
            headerClassName: 'align-left',
            title: localizationService.toLanguageString('myStudies.status'),
            cell: this.renderStatus,
            sortable: false,
            width: 100
          }
        ]}
      />
    );
  }

  handleSort(e) {
    this.props.sortMyStudiesCyclesList(e.sort);
  }

  renderCycle(e) {
    if (e.rowType === 'groupHeader') {
      return null;
    }
    const { className, dataItem } = e;
    const intlService = provideIntlService(this);
    const localizationService = provideLocalizationService(this);
    return (
      <td className={className}>
        <CycleContainer>
          <div>
            {dataItem.cycle} {dataItem.accreditedCredits > 0 && <InlineBadge>{`${localizationService.toLanguageString(`myStudies.accredited`)}: ${dataItem.accreditedCredits}`}</InlineBadge>}
          </div>
          <WrapLayout rowGap='0' columnGap='0'>
            <CaptionText>
              {dataItem.dateFrom && dataItem.dateTo &&
                DatePeriodFormatter(dataItem.dateFrom, dataItem.dateTo, intlService)
              }
            </CaptionText>
            {dataItem.cycleCredits &&
              <CaptionText>
                {`${localizationService.toLanguageString(`myStudies.credits`)}: ${dataItem.studentCredits}`}
              </CaptionText>
            }
          </WrapLayout>
        </CycleContainer>
      </td>
    );
  }

  renderCell(td, props) {
    if (td && td.props.children && props.rowType === 'groupHeader') {
      const localizationService = provideLocalizationService(this);
      const studyYear = props.dataItem.value == null
        ? `${localizationService.toLanguageString('myStudies.studyYear')} ${localizationService.toLanguageString('myStudies.notSpecify')}`
        : `${props.dataItem.value} ${localizationService.toLanguageString('myStudies.studyYear')}`;
      const children = (
        <GroupHeaderContainer>
          <span onClick={() => this.expandChange({ dataItem: props.dataItem })}>{td.props.children.props.children[0]}</span>
          <GroupHeaderStatsContainer>
            <NoWrapContainer>{`${studyYear}: `}</NoWrapContainer>
            <NoWrapContainer>{`${localizationService.toLanguageString('myStudies.assignedCredits')} - ${props.dataItem.assignedCredits}, `}</NoWrapContainer>
            <NoWrapContainer>{`${localizationService.toLanguageString('myStudies.finishedCredits')} - ${props.dataItem.finishedCredits}`}</NoWrapContainer>
            {props.dataItem.accreditedCredits > 0 &&
              <NoWrapContainer>{`, ${localizationService.toLanguageString('myStudies.accreditedCredits')} - ${props.dataItem.accreditedCredits}`}</NoWrapContainer>
            }
          </GroupHeaderStatsContainer>
        </GroupHeaderContainer>
      );
      return React.cloneElement(td, { ...td.props, colSpan: 3, onClick: () => this.expandChange({ dataItem: props.dataItem }), style: { paddingLeft: '12px' } }, children);
    }
    return td;
  }

  renderStatus(e) {
    if (e.rowType === 'groupHeader') {
      return null;
    }
    const { dataItem, className } = e;
    const localizationService = provideLocalizationService(this);
    return (
      <td className={className}>
        {dataItem.status &&
          <InlineBadge themeColor={getStudentCycleStatusColor(dataItem.status)}>
            {localizationService.toLanguageString('studyPlanCycleDepartmentStudentStatus.' + dataItem.status)}
          </InlineBadge>
        }
      </td>
    );
  }

  expandChange(event) {
    event.dataItem.expanded = !event.dataItem.expanded;
    this.forceUpdate();
  };

  getRowLink(dataItem) {
    return dataItem.id ? `/my_studies/cycle/${dataItem.id}` : null;
  }

  handleRowClick(e) {
    const href = this.getRowLink(e.dataItem);
    if (href) { this.props.navigate(href); }
  }
}

const mapStateToProps = (state) => ({
  myCycles: state.myStudiesCycleList.myCycles,
  loading: state.myStudiesCycleList.loading,
  sorting: state.myStudiesCycleList.sorting,
  studentId: state.app.currentStudies?.studentId
});

const mapDispatchToProps = (dispatch) => ({
  loadMyStudiesCycleList: (payload) => dispatch(loadMyStudiesCycleList(payload)),
  sortMyStudiesCyclesList: (payload) => dispatch(sortMyStudiesCyclesList(payload))
});

registerForLocalization(MyStudiesCycleList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyStudiesCycleList));
